import {liteClient as algoliasearch} from "algoliasearch/lite";
import instantsearch from 'instantsearch.js';
import {configure, hits, pagination, refinementList, searchBox, stats} from 'instantsearch.js/es/widgets';
import {history} from 'instantsearch.js/es/lib/routers';
import sanitizeHtml from 'sanitize-html';
import {h} from 'preact';

function setupSiteSearch() {
    const siteSearchConfig = window.siteSearchConfig || {};

    const appID = siteSearchConfig.algoliaAppId;
    const apiKey = siteSearchConfig.algoliaSearchKey;
    const indexName = siteSearchConfig.algoliaIndexName;
    const redirectSearchQuery = siteSearchConfig.redirectSearchQuery || null;

    function configError(message) {
        console.error(`Site Search Configuration Error: ${message}`);
        document.querySelector('#site-search-input')
            .innerHTML = `<div class="alert alert-danger">Configuration Error: ${message}</div>`;
    }

    if (!appID) {
        return configError('Algolia App ID is missing.');
    }

    if (!apiKey) {
        return configError('Algolia Search Key is missing.');
    }

    if (!indexName) {
        return configError('Algolia Index Name is missing.');
    }

    const suggestionElements = document.querySelectorAll('.site-search-suggestions');

    function getHonorImageUrl(hit) {
        const honorImageMap = siteSearchConfig.honorImageMap || {};
        const categoryName = hit.nomination?.award_category?.drupal_parent_award_name;
        if (!categoryName) return null;

        if (categoryName === 'Television Academy Honors') {
            const year = hit.nomination?.field_nominations_year;
            if (year >= 2021 && siteSearchConfig.honorTahPost2021Image) {
                return siteSearchConfig.honorTahPost2021Image;
            }
        }

        return honorImageMap[categoryName] || null;
    }

    function sanitizeTitle(title) {
        if (!title) {
            title = '';
        }
        return sanitizeHtml(title, {
            allowedTags: [
                'b', 'i', 'em', 'strong'
            ],
            allowedAttributes: {},
        });
    }

    function hideSuggestions() {
        suggestionElements.forEach(element => {
            element.style.display = 'none';
        });
    }

    function showSuggestions() {
        suggestionElements.forEach(element => {
            element.style.display = 'block';
        });
    }

    const client = algoliasearch(appID, apiKey);

    const search = instantsearch({
        indexName: indexName,
        searchClient: client,
        routing: {
            router: history(),
            stateMapping: {
                stateToRoute(uiState) {
                    const indexUiState = uiState[indexName];
                    return {
                        query: indexUiState.query,
                        collection: indexUiState.refinementList && indexUiState.refinementList['collection.title']
                            ? indexUiState.refinementList['collection.title'].join(',')
                            : undefined,
                        page: indexUiState.page,
                    };
                },
                routeToState(routeState) {
                    if (routeState['prod_Emmys_Site_Search']) {
                        // Compatibility with old URL structure
                        const oldState = routeState['prod_Emmys_Site_Search'];
                        const oldQuery = oldState.query;
                        const oldPage = oldState.page;
                        const oldCollection = oldState.menu && oldState.menu.type
                            ? oldState.menu.type
                            : undefined;

                        const collectionMapping = {
                            'nominations': 'Nomination',
                            'article': 'Article',
                            'celebrity': 'Bio',
                            'events': 'Event',
                            'photos': 'Photo',
                            'show': 'Show',
                            'brightcove': 'Video',
                        };

                        const newCollection = collectionMapping[oldCollection] || oldCollection;

                        return {
                            [indexName]: {
                                query: oldQuery,
                                page: oldPage,
                                refinementList: {
                                    'collection.title': newCollection
                                        ? [newCollection]
                                        : [],
                                },
                            },
                        }
                    }

                    return {
                        [indexName]: {
                            query: routeState.query,
                            page: routeState.page,
                            refinementList: {
                                'collection.title': routeState.collection
                                    ? routeState.collection.split(',')
                                    : [],
                            },
                        },
                    };
                },
            },
        },

        searchFunction(helper) {
            const container = document.querySelector('#site-search-results');
            if (helper.state.query === '') {
                container.style.display = 'none';
                showSuggestions();
            } else {
                helper.search();
                container.style.display = 'block';
                hideSuggestions();
            }
        }
    });

    search.addWidgets([
        configure({
            hitsPerPage: 10,
        }),
        searchBox({
            container: "#site-search-input",
            placeholder: 'Type to search',
            showReset: false,
            cssClasses: {
                form: 'form-group input-group search-input-group',
                input: 'form-control',
                submit: 'btn btn-primary',
            },
            templates: {
                submit({cssClasses}, {html}) {
                    return html`
                        <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
                            <g id="_1.-Foundations_Icons_ATAS_Icon_Search"
                               data-name="1.-Foundations/Icons/ATAS_Icon_Search">
                                <path class="cls-1"
                                      d="M31.21,28.38l.28-.38.42-.62.38-.5.42-.74c.06-.12.11-.24.16-.36.16-.32.3-.62.44-.94h0a14.45,14.45,0,0,0,.94-3.78h0a7.69,7.69,0,0,0,0-1,3.23,3.23,0,0,0,0-.44V17.38a.87.87,0,0,0-.1-.54A4,4,0,0,1,34,16c-.06-.28-.1-.38-.14-.58a7.36,7.36,0,0,0-.22-.74c-.08-.24-.16-.46-.26-.7s-.14-.38-.22-.56-.3-.66-.46-1a1.47,1.47,0,0,0-.14-.28,15,15,0,0,0-2.66-3.52h0a15,15,0,1,0-10.6,25.6h1a15.25,15.25,0,0,0,3.9-.78A4,4,0,0,0,20,30.28h-.68a11,11,0,1,1,9.14-17.1l.12.18a11,11,0,0,1,.9,1.74v.14a13.87,13.87,0,0,1,.58,2h0a11.26,11.26,0,0,1-.54,6v.2a9.77,9.77,0,0,1-.8,1.54c0,.14-.12.26-.2.38a11.07,11.07,0,0,1-1.4,1.72L25.67,28.5,40.89,43.72l2.84-2.84Z"/>
                            </g>
                        </svg>
                        <span class="d-none d-sm-inline-block ps-0 ps-sm-2">Search</span>`;
                },
            },
        }),

        refinementList({
            container: '#site-search-type-filter',
            attribute: 'collection.title',
            sortBy: ['name:asc'],
        }),

        stats({
            container: "#site-search-stats",
            templates: {
                text(data, {html}) {
                    let count = '';

                    if (data.nbHits === 0) {
                        count = 'No results';
                    } else if (data.nbHits === 1) {
                        count = 'Showing 1 result';
                    } else if (data.nbPages === 1) {
                        count = `Showing ${data.nbHits} results`;
                    } else {
                        const start = data.page * data.hitsPerPage + 1;
                        const end = Math.min((data.page + 1) * data.hitsPerPage, data.nbHits);

                        count = `Showing results ${start} - ${end} of ${data.nbHits}`;
                    }

                    return html`<span class="h4 d-block text-d2 mb-1">${count} for</span>
                    <h2 class="mb-5">${data.query}</h2>`;
                },
            },
        }),

        hits({
            container: "#site-search-hits",
            templates: {
                item(hit, {html, components}) {
                    const placeHolderSrc = siteSearchConfig.placeholderImageUrl;
                    const honorImageUrl = getHonorImageUrl(hit);

                    // Use title_wysiwyg for bio entries if available, otherwise use title
                    const displayTitle = hit.title_wysiwyg || hit.title;

                    let descriptionBody = html`
                        <a href="${hit.url ?? '#'}" class="h4 gold-d1 result-title">
                            ${h('span', {
                                dangerouslySetInnerHTML: {__html: sanitizeTitle(displayTitle)},
                            }, '')}
                        </a>
                        <p class="p1 result-secondary-text">${hit.secondary_text}</p>
                        <p class="p3 result-small-text">${hit.small_text}</p>
                    `;
                    let imageLinkUrl = hit.url ?? '#';

                    if (hit.nomination && hit.nomination.field_cast_or_show === 'show') {
                        descriptionBody = html`
                            <a href="${hit.url ?? '#'}" class="h4 gold-d1 result-title">
                                ${hit.nomination.award_category?.title || hit.title}
                            </a>
                            <p class="p1 result-secondary-text">
                                <a href="${hit.nomination.field_nominee_show_nr?.url || '#'}">
                                    ${hit.nomination.field_nominee_show_nr?.title}
                                </a>
                            </p>
                            <p class="p3 result-small-text">${hit.nomination.field_network?.title}</p>
                            <p class="p3 result-small-text">${hit.nomination.field_production_company?.title}</p>
                        `;
                        imageLinkUrl = hit.nomination.field_nominee_show_nr?.url ?? '#';
                    } else if (hit.nomination) {
                        descriptionBody = html`
                            <a href="${hit.url ?? '#'}" class="h4 gold-d1 result-title">
                                ${hit.nomination.award_category?.title || hit.title}
                            </a>
                            <p class="p1 result-secondary-text">
                                <a href="${hit.nomination.field_nominee_person_details?.[0]?.url || '#'}">
                                    ${hit.nomination.field_nominee_person_details?.[0]?.title}
                                </a>
                                ${hit.nomination.field_nominee_person_details?.[0]?.field_role_fc
                                    ? html`, as ${hit.nomination.field_nominee_person_details?.[0]?.field_role_fc}`
                                    : ''}
                            </p>
                            <p class="p1 result-secondary-text">
                                <a href="${hit.nomination.field_nominee_show_nr?.url || '#'}">
                                    ${hit.nomination.field_nominee_show_nr?.title}
                                </a>
                            </p>
                            <p class="p3 result-small-text">${hit.nomination.field_network?.title}</p>
                            <p class="p3 result-small-text">${hit.nomination.field_production_company?.title}</p>
                        `;
                        imageLinkUrl = hit.nomination.field_nominee_person_details?.[0]?.url ?? '#';
                    }

                    if (hit.label == 'Winner') {
                        return html`
                            <div class="search-result-item row g-0">
                                <div class="col-3 col-sm-2 col-md-4 col-lg-3 col-xl-2 result-image position-relative">
                                    <a href="${imageLinkUrl}"
                                       class="nomination-image-bio-page d-block position-relative text-center">
                                        ${honorImageUrl
                                            ? html`<img class="special-nomination-img" src="${honorImageUrl}"/>`
                                            : html`<img class="search-result-bio-image nomination-square-image" src="${hit.image_url || placeHolderSrc}"/>
                                                   <img class="search-result-bio-image nomination-inside-image" src="${hit.image_url || placeHolderSrc}"/>`
                                        }
                                    </a>
                                    ${!honorImageUrl ? html`<span class="winner-statuette"></span>` : ''}
                                    <svg class="${honorImageUrl ? 'd-none' : 'primary winner-statuette'}" xmlns="http://www.w3.org/2000/svg"
                                         xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
                                         viewBox="0 0 48 48" style="enable-background:new 0 0 48 48;"
                                         xml:space="preserve">
                                    <g id="Layer_1"><g><path d="M26.93,20.86c0.3,0.19,0.51,0.52,0.45,0.85c-0.04,0.22-0.21,0.42-0.41,0.51l-0.04,0C27.19,21.88,27.15,21.26,26.93,20.86zM25.84,11.97c0,0.43-0.04,0.77-0.1,1.11c-0.1-0.21-0.31-0.48-0.6-0.74c0.3-0.42,0.52-0.89,0.61-1.32C25.81,11.33,25.84,11.68,25.84,11.97z M20.48,15.47c-0.01-0.04-0.02-0.1-0.03-0.14c0.37-0.1,0.74-0.22,1.11-0.37c0.13-0.05,0.25-0.1,0.38-0.16c-0.02,0.19-0.02,0.4,0.04,0.66c0.04,0.19,0.09,0.52,0.14,0.81c-0.09,0.3-0.23,0.5-0.43,0.53c-0.36,0.06-0.77-0.27-1.22-0.89C20.48,15.72,20.49,15.53,20.48,15.47z M24.16,21.26c-0.39-0.31-1.41-1.31-2.15-1.98c-0.3-0.27-0.5-0.83-0.72-1.29c-0.22-0.47-0.81-1.14-0.87-1.72c0-0.01,0-0.05,0.01-0.11c0.54,0.71,1.06,1.08,1.49,0.95c0.11-0.03,0.2-0.09,0.28-0.18c0.09,0.58,0.16,1.62,0.4,2.11c0.41,0.82,1.55,2.11,1.6,2.18L24.16,21.26z M20.42,6.56c1.84,0,3.36,0.9,4.3,2.1c-0.52-0.29-1.31-0.48-2.36-0.46c-0.01-0.15-0.15-0.29-0.33-0.29c-0.19,0-0.33,0.15-0.33,0.33c0,0.18,0.15,0.33,0.33,0.33c0.16,0,0.3-0.12,0.32-0.27c1.47,0.01,2.61,0.48,2.98,1.41c0.29,0.71,0.07,1.55-0.53,2.36c-0.74-0.56-1.82-1.15-3.11-1.64c-0.68-1.86-1.58-3.28-2.34-3.75C19.71,6.6,20.06,6.56,20.42,6.56z M18.68,6.89c0.66-0.2,1.7,1.23,2.51,3.34c-0.06-0.02-0.13-0.05-0.19-0.07c-1.03-0.35-2.01-0.59-2.88-0.71C17.97,8.06,18.19,7.03,18.68,6.89z M16.02,15.12c0.86,0.46,2.09,0.61,3.44,0.41c0.07,0.16,0.15,0.33,0.26,0.52c0.05,0.09,0.25,0.66,0.49,1.31C18.48,17.3,16.96,16.43,16.02,15.12z M25.85,24.89c0.12,0,0.28,0.14,0.45,0.29c0.18,0.16,0.36,0.33,0.47,0.33c0.05,0,0.12-0.03,0.19-0.1c0.23-0.2,0.56-0.69,1.01-1.33c0.48-0.68,0.85-1.36,1.09-1.61c0.06-0.06,0.11-0.1,0.15-0.1c0.14,0,0.36,0.15,0.54,0.22c0.06,0.03,0.11,0.04,0.15,0.04c0.04,0,0.09-0.04,0.16-0.12c0.55-0.63,2.03-3.74,2.89-7.69c0.63-2.89,0.64-4.96,0.44-5.52c-0.03-0.1-0.07-0.14-0.12-0.15c-0.01,0-0.03,0-0.04,0c-0.01,0-0.01,0.01-0.01,0.01c-0.45,2.55-1.28,6.54-3.02,9.98c0,0-0.45-0.23-0.54-0.23c0,0-0.01,0-0.01,0c0,0,0,0-0.01,0c-0.09,0.1-0.85,1.74-1.57,2.97l-0.12-0.08c0.67-1.32,1.39-3.14,1.47-3.31c0-0.01,0.01-0.01,0.01-0.01c0,0,0.01,0,0.01,0c0.06,0.01,0.26,0.11,0.29,0.13c0.28-0.63,0.87-2.64,1.35-4.46c0.5-1.92,0.89-4.27,0.79-4.67c-0.01-0.03-0.03-0.05-0.07-0.06c-0.02,0-0.03,0.01-0.03,0.02c-0.13,0.48-0.97,3.04-1.54,4.68c-0.9,2.58-1.66,4.31-1.76,4.45c0,0.01-0.01,0.01-0.01,0.01c0,0-0.01,0-0.01,0c-0.07-0.02-0.32-0.14-0.38-0.16c-0.01,0-0.01,0-0.01,0c0,0,0,0-0.01,0.01c-0.06,0.07-0.27,0.49-0.34,0.64c0.22,0.17,0.55,0.66,0.39,1.22c-0.12,0.44-0.44,0.75-0.69,0.89l-0.04-0.01c0.38-0.48,0.17-1.15-0.02-1.42l0.06-0.03c0.05,0.02,0.21,0.14,0.3,0.41c0.07,0.22,0.05,0.41,0.04,0.48c0,0,0,0.01,0,0.01c0,0,0,0,0.01,0c0.05-0.06,0.32-0.69-0.07-1.2c-0.19-0.25-0.48-0.45-0.74-0.5c-0.38-0.08-0.58-0.08-0.78,0.05c-0.15,0.1-0.29,0.06-0.51-0.05c-0.07-0.03-0.13-0.05-0.16-0.03c-0.04,0.02-0.07,0.08-0.07,0.2c0,0.06-0.03,0.06-0.08,0.06c-0.04,0-0.1-0.02-0.14-0.02c-0.05,0-0.06,0.09-0.06,0.15c-0.02-0.01-0.07-0.02-0.11-0.01c-0.03,0.01-0.05,0.03-0.06,0.07c-0.02,0.11-0.04,0.12-0.15,0.16c-0.1,0.03-0.2,0.12-0.1,0.37c0.04,0.1,0.06,0.17,0.07,0.23c0.41,0.41,0.83,0.78,1.08,1.02c0.31,0.28,0.46,0.55,0.5,0.79c0.1,0.56-0.32,0.97-0.5,1.02c0,0-0.01,0-0.01,0c0,0,0-0.01,0-0.02c0.02-0.09,0.27-0.51,0.07-1.04c-0.09-0.23-0.26-0.47-0.56-0.72c-0.59-0.47-2.04-2.24-2.08-2.34c-0.11-0.27,0.05-0.41-0.27-1.34c-0.32-0.93-0.53-1.56-0.58-1.86c-0.06-0.39-0.07-0.47,0.06-0.74c0.14-0.3,0.25-0.64,0.23-0.79c-0.05-0.31-0.18-0.6-0.24-0.67c-0.1-0.66-0.27-1.37-0.49-2.1c-0.04-0.14-0.09-0.29-0.14-0.43c1.12,0.45,2.06,0.96,2.7,1.44c-0.46,0.54-0.98,0.98-1.71,1.41c0.03,0.1,0.06,0.22,0.08,0.32c0.01,0.05,0,0.13-0.01,0.2c0.83-0.47,1.46-1.01,1.98-1.65c0.41,0.37,0.6,0.71,0.51,0.98c-0.11,0.3-0.61,0.46-1.31,0.47l-0.04,0.09c0.56,0.03,1.1-0.03,1.42-0.2c-0.25,0.76-0.72,1.52-1.3,2.05l0.05,0.1c1.21-1.06,1.96-2.59,1.96-4.31c0-3.2-2.62-5.8-5.86-5.8c-3.23,0-5.86,2.6-5.86,5.8c0,3.18,2.58,5.76,5.79,5.8c0.24,0.65,0.51,1.35,0.71,1.76c0.55,1.14,1.97,2.21,2.23,2.42c0.11,0.09,0.25,0.21,0.24,0.39c-0.01,0.14-0.1,0.32-0.37,0.55c-0.23,0.2-0.65,0.47-0.73,0.69c-0.1,0.31,0.07,0.58,0.09,1.18c0.02,0.65-0.05,1.34-0.04,1.41c0,0,0,0.01,0,0.01c0,0,0,0,0,0c0.05-0.07,0.53-1.45,1.39-2l0.08,0.12c-1.12,0.97-1.14,2.6-1.1,3.8c0.02,0.73,0.45,2.66,0.96,3.59c0.77,1.4,1.3,2.08,2.14,3.95c0.84,1.87,1.03,3.4,1.11,4.03c0.08,0.63,0.25,1.42,0.25,1.68c0,0.14-0.1,0.2-0.21,0.25c-0.1,0.05-0.2,0.1-0.25,0.19c-0.02,0.04-0.03,0.07-0.03,0.09c0,0.1,0.12,0.15,0.28,0.18c0.07,0.07,0.19,0.12,0.45,0.11c0.3-0.01,0.46-0.06,0.67-0.18c0.19-0.11,0.3-0.26,0.24-0.43c-0.06-0.17,0.03-0.91,0.24-1.37c0.09-0.19,0.12-0.46,0.06-0.64c-0.04-0.13-0.13-0.22-0.29-0.18c-0.06,0.01-0.09-0.03-0.11-0.12c-0.07-0.34,0.11-1.27,0.16-1.39c0.03-0.09,0.1-0.15,0.19-0.2c0.19-0.1,0.47-0.13,0.75-0.19c0.39-0.08,0.78-0.2,0.88-0.59c0.02-0.07,0.08-0.13,0.16-0.19c0.18-0.14,0.46-0.28,0.61-0.41c0.06-0.06,0.1-0.12,0.1-0.17c0-0.04-0.02-0.07-0.06-0.11c-0.39-0.37-1.61-1.77-1.82-1.92c-0.01-0.01-0.03-0.02-0.03-0.01c-0.01,0.01,0,0.01,0.01,0.03c0.1,0.16,0.82,0.99,1.44,1.9c-0.26,0.11-0.61,0.34-0.69,0.52c0,0.01-0.01,0.01-0.01,0.01c-0.01,0-0.02-0.01-0.04-0.02c-0.21-0.23-1.05-2.01-1.77-3.5c-0.54-1.12-1.06-2.14-1.21-2.33c0-0.01-0.01-0.01-0.01-0.01c0,0,0,0.01,0,0.01c0,0.3,0.3,2.42,0.34,2.82l-0.11,0.01c-0.12-0.36-0.63-1.96-1.21-3.69c-0.47-1.39-1.13-2.12-1.72-2.54l0.13,0c0.44,0.15,1.02,0.53,1.19,0.61c0.03,0.01,0.05,0.02,0.05,0.02c0,0,0-0.02-0.02-0.04c-0.39-0.48-1.07-1.34-0.5-2.26C25.3,25.14,25.62,24.89,25.85,24.89z M18.05,7.11c-0.22,0.54-0.26,1.4-0.11,2.32c-1.03-0.14-1.85-0.1-2.35,0.09C16.14,8.43,17.02,7.6,18.05,7.11z M15,11.97c0-0.45,0.05-0.87,0.15-1.28c0.11,0.18,0.27,0.37,0.49,0.56c-0.28,0.34-0.49,0.69-0.64,1.04C15,12.18,15,12.08,15,11.97z M19.93,14.95c-0.09-0.12-0.19-0.25-0.29-0.33c-0.1-0.07-0.23-0.08-0.33-0.05c-0.1,0.03-0.17,0.08-0.16,0.12c0.01,0.04,0.05,0.17,0.12,0.38c-0.9,0.13-1.73,0.09-2.41-0.09c-0.62-0.17-1.11-0.46-1.42-0.87c-0.13-0.3-0.24-0.64-0.31-0.95c0.01-0.63,0.24-1.24,0.65-1.81c0.75,0.61,2.06,1.27,3.65,1.84c-0.01,0.05-0.02,0.1-0.02,0.16c0,0.37,0.3,0.67,0.67,0.67c0.37,0,0.67-0.3,0.67-0.67c0-0.37-0.3-0.67-0.67-0.67c-0.3,0-0.52,0.19-0.62,0.4c-1.6-0.59-2.87-1.3-3.56-1.9c0.27-0.34,0.61-0.68,0.99-0.99l-0.11-0.03c-0.39,0.3-0.71,0.6-1,0.92c-0.34-0.34-0.5-0.65-0.43-0.87c0.15-0.46,1.18-0.59,2.64-0.36c0.05,0.21,0.11,0.46,0.17,0.67l0.09-0.02c-0.04-0.21-0.08-0.42-0.11-0.63c0.81,0.13,1.74,0.37,2.71,0.69c0.17,0.06,0.33,0.12,0.49,0.17c0.08,0.23,0.16,0.47,0.23,0.72c0.23,0.75,0.39,1.47,0.5,2.14c-0.04,0.04-0.07,0.09-0.09,0.14c-0.04,0.16-0.02,0.33,0,0.52c-0.2,0.09-0.4,0.18-0.61,0.26C20.91,14.7,20.41,14.84,19.93,14.95z M18.41,10.64c-0.23,0-0.41,0.18-0.41,0.4c0,0.22,0.18,0.4,0.41,0.4c0.23,0,0.41-0.18,0.41-0.4C18.82,10.82,18.64,10.64,18.41,10.64z"/></g></g></svg>
                                </div>
                                <div class="col-9 col-sm-10 col-md-8 col-lg-9 col-xl-10">
                                    <div class="bg-ui-d-panel pb-3 pt-2 px-4 border-radius-8">
                                        <label
                                            class="d-block large result-label ${hit.label == 'Winner' ? 'text-d1' : 'text-d3'}">
                                            ${hit.label}
                                        </label>
                                        ${descriptionBody}
                                    </div>
                                </div>
                            </div>`;
                    } else {
                        return html`
                            <div class="search-result-item row g-0">
                                <div class="col-3 col-sm-2 col-md-4 col-lg-3 col-xl-2 result-image">
                                    <a href="${imageLinkUrl}"
                                       class="nomination-image-bio-page d-block position-relative text-center">
                                        ${honorImageUrl
                                            ? html`<img class="special-nomination-img" src="${honorImageUrl}"/>`
                                            : html`<img class="search-result-bio-image nomination-inside-image" src="${hit.image_url || placeHolderSrc}"/>
                                                   <img class="search-result-bio-image nomination-square-image" src="${hit.image_url || placeHolderSrc}"/>`
                                        }
                                    </a>
                                </div>
                                <div class="col-9 col-sm-10 col-md-8 col-lg-9 col-xl-10">
                                    <div class="bg-ui-d-panel pb-3 pt-2 px-4 border-radius-8">
                                        <label
                                            class="d-block large result-label ${hit.label == 'Winner' ? 'text-d1' : 'text-d3'}">
                                            ${hit.label}
                                        </label>
                                        ${descriptionBody}
                                    </div>
                                </div>
                            </div>`;
                    }
                },
            },
        }),

        pagination({
            container: '#site-search-pagination',
        }),
    ]);

    if (redirectSearchQuery) {
        const comingFromUrl = new URL(window.location.href);
        const comingFromUrlRelative = comingFromUrl.pathname + comingFromUrl.search;
        window.history.replaceState({}, '', `/site--search?query=${encodeURIComponent(redirectSearchQuery)}&origin=${encodeURIComponent(comingFromUrlRelative)}`);
    }

    search.start();

    const suggestedQueryTags = document.querySelectorAll('.site-search-suggested-query');
    suggestedQueryTags.forEach(tag => {
        tag.addEventListener('click', (ev) => {
            ev.preventDefault();
            search.helper.setQuery((tag.textContent || '').trim()).search();
        });
    });
}

if (document.querySelector('#site-search')) {
    setupSiteSearch();
}
